import { AppBarProps } from '@mui/material/AppBar';
import { Box } from '@atoms/layout';
import { useLocationStore } from '@libs/store/locations';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { SelectChangeEvent, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { getTimeZones } from '@vvo/tzdb';
import dayjs from 'dayjs';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { HeaderSelect } from '@atoms/inputs';

const WeekDayStartMap: Record<string, number> = {
  Saturday: 6,
  Sunday: 0,
  Monday: 1,
};

export const HeaderLocationSelector = ({ ...props }: AppBarProps) => {
  const [timezoneData, setTimezoneData] = useState({
    label: '',
    tooltip: '',
    name: TIMEZONE_FALLBACK,
    abbreviation: '',
  });
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const {
    locationsSimple,
    initiatedSimple,
    loadSimple,
    loadingSimple: locationsLoading,
    current: currentLocation,
    currentLocationID,
    setCurrentLocation,
  } = useLocationStore();

  const locationOptions = useMemo(
    () =>
      locationsSimple
        .filter((location) => location.status === 'Active')
        .map((loc) => ({
          key: loc.id,
          value: loc.id,
          label: loc.name,
        })),
    [locationsSimple],
  );

  const locationValue = currentLocationID || locationOptions[0]?.value || '';

  useEffect(() => {
    const intervalId = setInterval(() => {
      setLastUpdate(new Date());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!initiatedSimple) {
      loadSimple();
    }
  }, [initiatedSimple, loadSimple]);

  useEffect(() => {
    if (!currentLocation) {
      if (currentLocationID && locationOptions.some((loc) => loc.value === currentLocationID)) {
        setCurrentLocation(currentLocationID);
      } else if (locationOptions.length > 0) {
        setCurrentLocation(locationOptions[0].value);
      }
    }
  }, [currentLocation, currentLocationID, locationOptions, setCurrentLocation]);

  useEffect(() => {
    if (currentLocation) {
      dayjs.updateLocale('en', {
        weekStart: WeekDayStartMap[currentLocation.workWeekDayStart || 'Sunday'],
      });

      const currentTZData = getTimeZones({ includeUtc: true }).find((tz) => tz.name === currentLocation.timezone);

      if (currentTZData) {
        const shift = dayjs().tz(currentTZData.name).format('hh:mm A');
        setTimezoneData({
          tooltip: currentTZData.currentTimeFormat,
          name: currentTZData.name,
          abbreviation: currentTZData.abbreviation,
          label: `${shift} (${currentTZData.abbreviation})`,
        });
      }
    }
  }, [currentLocation]);

  useEffect(() => {
    if (timezoneData.name && timezoneData.abbreviation) {
      const shift = dayjs().tz(timezoneData.name).format('hh:mm A');
      setTimezoneData((prev) => ({
        ...prev,
        label: `${shift} (${prev.abbreviation})`,
      }));
    }
  }, [lastUpdate, timezoneData.name, timezoneData.abbreviation]);

  const handleLocationChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      setCurrentLocation(e.target.value);
    },
    [setCurrentLocation],
  );

  const loading = locationsLoading;
  const txPreparing = loading || !timezoneData.abbreviation || !timezoneData.label;

  return (
    <Stack direction="row">
      <Box>
        {loading ? (
          <Skeleton variant="rounded" animation="wave" height={40} width={220} />
        ) : (
          <HeaderSelect
            options={locationOptions}
            fullWidth
            sx={{ minWidth: '220px' }}
            value={locationValue}
            onChange={handleLocationChange}
          />
        )}
      </Box>
      <Stack direction="column" sx={{ minWidth: '120px' }} spacing={0.5} ml={2}>
        <Box flex={1} />
        {txPreparing ? (
          <Skeleton width="150px" />
        ) : (
          <Tooltip title={timezoneData.tooltip}>
            <Typography variant="subtitle1">{timezoneData.label}</Typography>
          </Tooltip>
        )}
        <Box flex={1} />
      </Stack>
    </Stack>
  );
};
